// Attribution: https://jasonwatmore.com/post/2020/04/11/react-hooks-bootstrap-alert-notifications

// Dependencies
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';

// Material
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

// Services
import { alertService, AlertType } from 'services/alert';

function AlertCommon({ id = 'default-id', fade = true }) {

	const [alerts, setAlerts] = useState([]);
	const location = useLocation();

	useEffect(() => {
		// subscribe to new alert notifications
		const subscription = alertService.onAlert(id)
			.subscribe(alert => {
				// clear alerts when an empty alert is received
				if (!alert?.message) {
					setAlerts(alerts => {
						// filter out alerts without 'keepAfterRouteChange' flag
						const filteredAlerts = alerts.filter(x => x.keepAfterRouteChange);

						// remove 'keepAfterRouteChange' flag on the rest
						filteredAlerts.forEach(x => delete x.keepAfterRouteChange);
						return filteredAlerts;
					});
				} else {
					// add alert to array
					setAlerts(alerts => ([...alerts, alert]));

					// auto close alert if required (6seconds as per accessibility)
					if (alert.autoClose) {
						setTimeout(() => removeAlert(alert), 6000);
					}
				}
			});

		// clean up function that runs when the component unmounts
		return () => {
			// unsubscribe to avoid memory leaks
			subscription.unsubscribe();
			alertService.clear(id);
		};
	}, [location]);

	function removeAlert(alert) {
		if (fade) {
			// fade out alert
			const alertWithFade = { ...alert, fade: true };
			setAlerts(alerts => alerts.map(x => x === alert ? alertWithFade : x));

			// remove alert after faded out
			setTimeout(() => {
				setAlerts(alerts => alerts.filter(x => x !== alertWithFade));
			}, 250);
		} else {
			// remove alert
			setAlerts(alerts => alerts.filter(x => x !== alert));
		}
	}

	function cssClasses(alert) {
		if (!alert) return;

		const classes = [];

		const alertTypeClass = {
			[AlertType.Success]: 'success',
			[AlertType.Error]: 'error',
			[AlertType.Info]: 'info',
			[AlertType.Warning]: 'warning'
		}

		classes.push(alertTypeClass[alert.type]);

		return classes.join(' ');
	}

	if (!alerts.length) return null;

	return (
		<div>
			{alerts.map((alert, index) =>
				<Alert
					key={index}
					severity={cssClasses(alert)}
					onClose={() => { removeAlert(alert) }}
					variant="outlined"
					sx={{ borderLeft: 15, marginBottom: 1 }}
				>
					<AlertTitle sx={{ fontWeight: 'bold' }}>{alert.type}</AlertTitle>
					<span>{alert.message}</span>
				</Alert>
			)}
		</div>
	);
}

export { AlertCommon };
